// Shared character counter for content editors.
// Mirrors server/lib/contentIntelligence.js PLATFORM_PLAYBOOKS charLimit values.

export const PLATFORM_CHAR_LIMITS = {
  x: 280,
  twitter: 280,
  linkedin: 3000,
  instagram: 2200,
  facebook: 63206,
  tiktok: 2200,
  youtube: 5000,
  pinterest: 500,
  reddit: 40000,
  blog: 100000,
  website: 100000,
  general: 10000,
};

// Soft thresholds: platforms where long posts hurt engagement even below the hard limit.
// These are shown as a "sweet spot" warning rather than a block.
export const PLATFORM_SOFT_LIMITS = {
  x: 200,          // best engagement under 200
  linkedin: 210,   // mobile truncation — first 210 chars are everything
  instagram: 125,  // "more" cutoff at 125
  facebook: 80,    // best engagement under 80
  tiktok: 150,
  youtube: 125,    // first 2-3 lines shown in search
  pinterest: 200,  // keyword-rich descriptions read best under ~200
  reddit: 1500,    // long-form is fine, but front-load the value
};

export function getCharLimit(platform) {
  const key = String(platform || 'general').toLowerCase();
  return PLATFORM_CHAR_LIMITS[key] ?? PLATFORM_CHAR_LIMITS.general;
}

export function getSoftLimit(platform) {
  const key = String(platform || 'general').toLowerCase();
  return PLATFORM_SOFT_LIMITS[key] || null;
}

/**
 * Render the counter HTML. Call once when you render the textarea.
 * @param {Object} opts
 * @param {string} opts.platform - lowercase platform key
 * @param {number} opts.initialLength - starting character count (e.g. draftText.length)
 * @param {string} opts.counterId - DOM id for the counter element
 */
export function renderCharCounter({ platform, initialLength = 0, counterId = 'char-counter' }) {
  const limit = getCharLimit(platform);
  const soft = getSoftLimit(platform);
  const pct = limit > 0 ? Math.min(100, (initialLength / limit) * 100) : 0;
  const state = getCounterState(initialLength, limit, soft);
  const softHint = soft
    ? `<span class="char-counter-hint">Sweet spot: ${soft}</span>`
    : '';
  return `
    <div class="char-counter ${state}" id="${counterId}" data-platform="${platform || 'general'}" data-limit="${limit}" data-soft="${soft || ''}">
      <div class="char-counter-row">
        <span class="char-counter-count"><strong>${initialLength.toLocaleString()}</strong> / ${limit.toLocaleString()}</span>
        ${softHint}
      </div>
      <div class="char-counter-bar"><div class="char-counter-bar-fill" style="width:${pct}%"></div></div>
    </div>
  `;
}

function getCounterState(length, limit, soft) {
  if (length > limit) return 'char-counter--over';
  if (length > limit * 0.9) return 'char-counter--warn';
  if (soft && length > soft) return 'char-counter--soft';
  return 'char-counter--ok';
}

/**
 * Update an existing counter in place. Call from the textarea input handler.
 * @param {HTMLElement|string} counterOrId - element or id
 * @param {number} length - current text length
 */
export function updateCharCounter(counterOrId, length) {
  const el = typeof counterOrId === 'string'
    ? document.getElementById(counterOrId)
    : counterOrId;
  if (!el) return;
  const limit = Number(el.dataset.limit) || 10000;
  const soft = el.dataset.soft ? Number(el.dataset.soft) : null;
  const pct = limit > 0 ? Math.min(100, (length / limit) * 100) : 0;
  const state = getCounterState(length, limit, soft);

  el.classList.remove('char-counter--ok', 'char-counter--soft', 'char-counter--warn', 'char-counter--over');
  el.classList.add(state);

  const countEl = el.querySelector('.char-counter-count');
  if (countEl) {
    countEl.innerHTML = `<strong>${length.toLocaleString()}</strong> / ${limit.toLocaleString()}${length > limit ? ' — over limit' : ''}`;
  }
  const fill = el.querySelector('.char-counter-bar-fill');
  if (fill) fill.style.width = pct + '%';
}

/**
 * Change the platform for an existing counter (e.g. when user switches platform selector).
 */
export function setCharCounterPlatform(counterOrId, platform, currentLength = 0) {
  const el = typeof counterOrId === 'string'
    ? document.getElementById(counterOrId)
    : counterOrId;
  if (!el) return;
  const limit = getCharLimit(platform);
  const soft = getSoftLimit(platform);
  el.dataset.platform = platform || 'general';
  el.dataset.limit = String(limit);
  el.dataset.soft = soft ? String(soft) : '';
  updateCharCounter(el, currentLength);
}
